
import React, { useState, useEffect } from 'react';

import { apiConfig } from 'config/apiConfig'

import useFetchWithMsal from 'hooks/useFetchWithMsal';
import { ProgressIndicator, useProgressIndicator } from 'components/Elements/ProgressIndicator';
import { useCardroomContext } from 'features';
import DataTable from 'components/Elements/DataTable';
import ParticipantCell from 'components/Elements/DataTable/components/Cells/ParticipantCell';
import TextCell from 'components/Elements/DataTable/components/Cells/TextCell';
import { Card, CardActions, CardContent, Grid, Icon, useMediaQuery, useTheme } from '@mui/material';
import whiteCurved from "assets/images/curved-images/white-curved.jpeg";
import SoftBox from 'components/SoftBox';
import MiniStatisticsCard from 'components/Elements/MiniStatisticsCard';
import { useDialog } from 'components/Elements/Dialog/common';
import { ClubPlayerEditorDialog } from 'layouts/Club/components/Player';
import SoftButton from 'components/SoftButton';
import IconCell from 'components/Elements/DataTable/components/Cells/IconCell';
import { useClubDataLoader } from 'layouts/Club/Union/components/common';

const ClubPlayers = ({ cardroom, club }) => {

    function preparePlayersForPresentation(players, table) {
        let rows = [];

        for (var i = 0; i < players.length; i++) {
            let playerImage = players[i].name.substring(0, 1);

            rows.push({
                name: [players[i].name, { image: playerImage }],
                displayName: players[i].name,
                displayNameNoCase: players[i].name.toLowerCase(),
                nickname: players[i].nickname,
                appPlayerId: players[i].appPlayerId,
                agentName: players[i].agent ? players[i].agent.name : "None",
                agentId: players[i].agent ? players[i].agent.appAgentId : "None",
                superAgentName: players[i].agent && players[i].agent.superAgent ? players[i].agent.superAgent.name : "None",
                superAgentId: players[i].agent && players[i].agent.superAgent ? players[i].agent.superAgent.appAgentId : "None",
                sourceAppName: players[i].app ? players[i].app.name : "None",
                player: players[i],
                rebate: players[i].rebateSchedule
            });
        }

        if (rows.length > 0) {
            rows.sort(function (a, b) {
                if (a.displayNameNoCase < b.displayNameNoCase) { return -1; }
                if (a.displayNameNoCase > b.displayNameNoCase) { return 1; }
                return 0;
            });
        }

        table.rows = rows;

        return table;
    }

    function GetEmptySchema(onOpenPlayerEdit) {
        return {
            columns: [
                {
                    Header: "Player",
                    accessor: "name",
                    Cell: ({ value: [name, data], row }) => (
                        <ParticipantCell image={data.image} color={data.color || "dark"} name={name} selectable={false} checked={false} participant={row && row.original && row.original.player ? row.original.player : null} onClick={onOpenPlayerEdit} />
                    ),
                },
                { Header: "Player Id", accessor: "appPlayerId", Cell: ({ value }) => <TextCell value={value} /> },
                { Header: "Player Nickname", accessor: "nickname", Cell: ({ value }) => <TextCell value={value} /> },
                { Header: "Agent", accessor: "agentName", Cell: ({ value }) => <TextCell value={value} /> },
                { Header: "Agent Id", accessor: "agentId", Cell: ({ value }) => <TextCell value={value} /> },
                { Header: "Super Agent", accessor: "superAgentName", Cell: ({ value }) => <TextCell value={value} /> },
                { Header: "Super Agent Id", accessor: "superAgentId", Cell: ({ value }) => <TextCell value={value} /> },
                { Header: "Rebate", accessor: "rebate", Cell: ({ value }) => <IconCell value={value ? "Yes" : "No"} icon={value ? "check" : "block"} color={value ? "success" : "warning"} /> },
                { Header: "Source", accessor: "sourceAppName", Cell: ({ value }) => <TextCell value={value} /> },
            ],
            rows: []
        };
    }

    const switchToMobileOnSize = "md";

    const theme = useTheme();
    const isMobileUi = !useMediaQuery(theme.breakpoints.up(switchToMobileOnSize));
    const isXs = !useMediaQuery(theme.breakpoints.up("sm"));


    const [players, setPlayers] = useState(null);

    const [playersTable, setPlayersTable] = useState(null);

    const [retryCounter, setRetryCounter] = useState(0);

    function handleRetry() {
        setRetryCounter(retryCounter + 1);
    }

    const [showProgress, progressIndicatorProps] = useProgressIndicator("wait", "Loading club players...", handleRetry)

    const [context, actions, features] = useCardroomContext(cardroom);

    const [openPlayerEditorDialog, playerEditorDialogProps] = useDialog();

    const [currentPlayer, setCurrentPlayer] = useState(null);

    function openPlayerEditor(player) {
        if (!canEditPlayerProfile()) return;

        if (player) {
            setCurrentPlayer(player);
            openPlayerEditorDialog();
        }
    }

    function canEditPlayerProfile() {
        if (!context) return false;

        return context.isAllowed(actions.club.club.edit);
    }

    function playerHasbeenUpdated(p) {
        if (playersTable && playersTable.rows) {
            for (var i = 0; i < playersTable.rows.length; i++) {
                if (playersTable.rows[i].player && playersTable.rows[i].player.id === p.id) {
                    playersTable.rows[i].player.rebateSchedule = p.rebateSchedule;
                    playersTable.rows[i].rebate = p.rebateSchedule;
                    //alert("updated");
                    break;
                }
            }
        }
    }

    const clubRequest = useFetchWithMsal({
        scopes: apiConfig.club.scopes.read,
    });

    const clubDataLoader = useClubDataLoader();

    useEffect(() => {
        if (!players) {
            progressIndicatorProps.setMode("wait");
            progressIndicatorProps.setMessage("Loading club players...")
            showProgress();

            clubDataLoader.entity.loadPlayers(clubRequest, cardroom, club, true, (response) => {
                setPlayers(response);
                setPlayersTable(null);
            }, (ex) => {
                progressIndicatorProps.setMode("errorWithRetry");
                progressIndicatorProps.setMessage("Unable to load club players, please try again...");
            });

        }
    }, [clubRequest.execute, players, retryCounter]) //cardroomListData 

    useEffect(() => {
        if (players && !playersTable) {
            setPlayersTable(preparePlayersForPresentation(players, GetEmptySchema(canEditPlayerProfile() ? openPlayerEditor : null)));
            progressIndicatorProps.close();
        }
    }, [players, playersTable]);


    function renderMobileRow(columns, row, key, collapsibleDetailAccessor) {
        const color = "secondary";

        const hasAgent = row.cells[4].value != "None";
        const hasSuperAgent = row.cells[6].value != "None";

        return <Card
            sx={({ functions: { linearGradient, rgba }, palette: { gradients } }) => ({
                backgroundImage: gradients[color]
                    ? `${linearGradient(
                        rgba(gradients[color].main, 0.85),
                        rgba(gradients[color].state, 0.85)
                    )}, url(${whiteCurved})`
                    : `${linearGradient(
                        rgba(gradients[color].main, 0.85),
                        rgba(gradients[color].state, 0.85)
                    )}, url(${whiteCurved})`,
                backgroundSize: "cover",
                backgroundPosition: "50%",
                overflow: "visible",
            })}
        >
            <CardContent>
                <SoftBox pt={2} textAlign="center">
                    <MiniStatisticsCard
                        bgColor="white"
                        title={{ text: row.cells[1].value, fontWeight: "medium" }}
                        count={row.cells[0].value[0]}
                        icon={{ color: "dark", component: "person" }}
                        direction={"left"}
                        useNumericFormatter={false}
                    />
                </SoftBox>
                {hasAgent ?
                    < SoftBox pt={2} textAlign="center">

                        <Grid container ml="auto" mr="auto" xs={12}>
                            <Grid item xs={hasSuperAgent ? 6 : 12} pr={0.25} pt={{ xs: 1, sm: 0 }} pb={{ xs: 1, sm: 0 }}>
                                <MiniStatisticsCard
                                    bgColor="white"
                                    title={{ text: row.cells[4].value, fontWeight: "medium" }}
                                    count={row.cells[3].value}
                                    icon={isXs && hasSuperAgent ? null : { color: "dark", component: "support_agent" }}
                                    direction={"left"}
                                    useNumericFormatter={false}
                                />
                            </Grid>
                            {hasSuperAgent ?
                                <Grid item xs={hasSuperAgent ? 6 : 12} pl={0.25} pt={{ xs: 1, sm: 0 }} pb={{ xs: 1, sm: 0 }}>
                                    <MiniStatisticsCard
                                        bgColor="white"
                                        title={{ text: row.cells[6].value, fontWeight: "medium" }}
                                        count={row.cells[5].value}
                                        icon={isXs ? null : { color: "dark", component: "support_agent" }}
                                        direction={"right"}
                                        useNumericFormatter={false}
                                    />
                                </Grid> : null}
                        </Grid>

                    </SoftBox> : null}

            </CardContent>
            <CardActions>
                <Grid item xs={12} p={1}>
                    <SoftButton ml="auto" mr="auto"
                        variant="gradient"
                        color={"dark"}
                        fullWidth
                        disabled={!canEditPlayerProfile()}
                        onClick={() => openPlayerEditor(row.original.player)}
                    >
                        SETTINGS&nbsp;
                        <Icon sx={{ fontWeight: "bold" }}>settings</Icon>
                    </SoftButton>
                </Grid>
            </CardActions>

        </Card >;

    }

    return <Card>
        <ProgressIndicator {...progressIndicatorProps} />

        {(cardroom && club) && !progressIndicatorProps.visible ? <>
            {canEditPlayerProfile() ? <ClubPlayerEditorDialog {...playerEditorDialogProps} cardroom={cardroom} club={club} player={currentPlayer} onPlayerUpdated={playerHasbeenUpdated} /> : null}

            {playersTable ?
                <DataTable table={playersTable} canSearch entriesPerPage={{ defaultValue: 10, visible: !isMobileUi }} pagination={{ color: "dark" }}
                    onRenderRow={renderMobileRow} useCustomRenderSize={switchToMobileOnSize}
                /> : null}
        </> : null}
    </Card>;
};

export default ClubPlayers